import React from 'react';
import styled from 'styled-components';
import type { IStaffUI } from 'types/staff';
import StaffAvatar from 'components/StaffAvatar';
import { IconCheck } from './icons';

interface Props {
  staffs: IStaffUI[];
  selectedStaffIds: number[];
  perStaffCounts: Record<number, number>;
  onToggle: (staffId: number) => void;
  onClear: () => void;
}

/**
 * Multi-select avatar strip. Empty selection = all staff, so Clear is the reset
 * back to "all" and no explicit Select All action is needed.
 */
const StaffFilterStrip: React.FC<Props> = ({ staffs, selectedStaffIds, perStaffCounts, onToggle, onClear }) => {
  const selCount = selectedStaffIds.length;
  const countLabel = selCount === 0 ? '· all' : `· ${selCount} selected`;

  return (
    <Wrapper>
      <Head>
        <Label>
          Staff <span className="accent">{countLabel}</span>
        </Label>
        <div className="actions">
          <GhostBtn onClick={onClear}>Clear</GhostBtn>
        </div>
      </Head>
      <Row>
        {staffs.map((s, i) => {
          const staffId = Number(s.id);
          const active = selCount > 0 && selectedStaffIds.includes(staffId);
          const dim = selCount > 0 && !active;
          const count = perStaffCounts[staffId] || 0;
          return (
            <StaffBtn key={s.id} onClick={() => onToggle(staffId)}>
              <AvatarWrap>
                <StaffAvatar
                  name={s.name}
                  avatar={s.avatar}
                  gradIndex={i}
                  size={52}
                  fontSize={16}
                  style={{
                    boxShadow: active ? '0 0 0 3px #fff, 0 0 0 6px var(--acc)' : '0 0 0 1px var(--border-1)',
                    opacity: dim ? 0.55 : 1,
                  }}
                />
                {active && (
                  <Badge>
                    <IconCheck size={11} color="#fff" strokeWidth={3.4} />
                  </Badge>
                )}
                {count > 0 && <CountBadge>{count}</CountBadge>}
              </AvatarWrap>
              <Name $active={active}>{s.name}</Name>
            </StaffBtn>
          );
        })}
      </Row>
    </Wrapper>
  );
};

export default StaffFilterStrip;

const Wrapper = styled.div`
  padding: 16px 0 14px;
  border-bottom: 1px solid var(--border-1);
  flex-shrink: 0;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 0 24px;
  .actions { display: flex; align-items: center; gap: 8px; }
`;

const Label = styled.span`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-1);
  .accent { color: var(--acc); }
`;

const GhostBtn = styled.button`
  padding: 6px 12px;
  border-radius: var(--r-md);
  border: 1px solid var(--border-1);
  background: var(--bg-surface);
  color: var(--fg-2);
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  &:hover { background: var(--bg-subtle); }
`;

const Row = styled.div`
  display: flex;
  gap: 6px;
  overflow-x: auto;
  padding: 10px 24px 12px;
`;

const StaffBtn = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  padding: 8px 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  width: 86px;
  font-family: inherit;
`;

const AvatarWrap = styled.span`
  position: relative;
  display: inline-flex;
`;

const Badge = styled.span`
  position: absolute;
  top: -3px;
  right: -3px;
  width: 19px;
  height: 19px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  background: var(--acc);
  border: 2px solid #fff;
`;

// Entry count for the selected day. Sized to stay legible on top of photo
// avatars, with a white ring + shadow so it reads as a separate layer.
const CountBadge = styled.span`
  position: absolute;
  bottom: -4px;
  right: -6px;
  min-width: 26px;
  height: 26px;
  padding: 0 6px;
  box-sizing: border-box;
  border-radius: 999px;
  background: var(--brand-navy);
  color: #fff;
  font-size: 14px;
  font-weight: 800;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2.5px solid #fff;
  box-shadow: 0 2px 6px rgba(0, 24, 72, 0.35);
`;

const Name = styled.span<{ $active?: boolean }>`
  font-size: 12px;
  line-height: 1.2;
  text-align: center;
  max-width: 82px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${p => (p.$active ? 'var(--fg-1)' : 'var(--fg-3)')};
  font-weight: ${p => (p.$active ? 700 : 500)};
`;
